import { useEffect } from 'react';
import useFetch from './useFetchAPI';

interface FeatureFlag {
  FeatureFlagID: number;
  FeatureName: string;
  Description: string | null;
  Status: string;
  CreatedDate: string;
  IsGloballyEnabled: boolean;
  IsEnabled: boolean;
  EnabledDate: string | null;
}

interface UseGetCustomerFeatureFlagsReturn {
  featureFlags: FeatureFlag[] | null;
  isLoading: boolean;
  error: Error | null;
  isFeatureEnabled: (featureName: string) => boolean;
}

const useGetCustomerFeatureFlags = (customerId: number | null): UseGetCustomerFeatureFlagsReturn => {
  const { fetchAPI, isLoading, serverError, apiData } = useFetch({ scope: 'AppUser' });

  // Fetch once when customerId changes, not when fetchAPI changes
  useEffect(() => {
    if (!customerId || customerId <= 0) {
      return;
    }

    fetchAPI({
      url: `general/customer/${customerId}/features`,
      method: 'GET',
    }).catch((err) => {
      console.error('Error fetching feature flags:', err);
    });
  }, [customerId]); // Only depend on customerId, NOT fetchAPI

  const isFeatureEnabled = (featureName: string): boolean => {
    if (!apiData || !Array.isArray(apiData)) return false;
    const feature = (apiData as FeatureFlag[]).find(f => f.FeatureName === featureName);
    return feature ? feature.IsEnabled : false;
  };

  return {
    featureFlags: Array.isArray(apiData) ? (apiData as FeatureFlag[]) : null,
    isLoading,
    error: serverError,
    isFeatureEnabled,
  };
};

export default useGetCustomerFeatureFlags;
